// src/pages/userprofile/ProfileSettingsPage.tsx
// Profile management: hero header with banner/avatar + personal info + avatar gallery.

import { useState, useEffect, useCallback } from 'react';
import { observer } from '@react';
import { useUserprofile, useUserprofileAvatar, useUserprofileBanner, useUserprofileSetting, useI18n, useAuth } from '@react/hooks';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, ErrorAlert,
} from '@react/components';
import { Textarea } from '@react/components';
import { AvatarViewer } from '../../components/AvatarViewer';
import { cn } from '@core/ui';
import {
  validateProfile, validateAvatar, validateBanner, formatProfileStatus, getProfileInitials,
  genderOptions, bannerGradients,
} from '../../features/userprofile/logic';
import type { Userprofile, Avatar, Banner } from '@core/types/userprofile';
import { Select } from '@/components/Select';
import { ListUserprofilesForService } from '@core/api/endpoints/userprofile/list_userprofiles_for_service';
import { GetUserprofileActive } from '@core/api/endpoints/userprofile/get_userprofile_active';
import { EntityAvatar } from '@/components/EntityAvatar';

// ─── Profile Header ─────────────────────────────────────────────
const ProfileHeader = observer(({ profile, avatarUrl, bannerUrl, bannerGradient, onAvatarClick, onAvatarUpload, onBannerChange, onBannerRemove, t }: {
  profile: Userprofile | null;
  avatarUrl: string | undefined;
  bannerUrl: string | undefined;
  bannerGradient: string;
  onAvatarClick: () => void;
  onAvatarUpload: (file: File) => void;
  onBannerChange: (file: File) => void;
  onBannerRemove: () => void;
  t: (key: string) => string;
}) => {
  const initials = profile ? getProfileInitials(profile.first_name, profile.last_name) : '';
  const status = formatProfileStatus(profile?.status ?? 'PROFILE_STATUS_ACTIVE');
  const displayName = profile?.display_name || `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
  return (
    <Card className="overflow-hidden">
      {/* Banner */}
      <div className="relative group/banner">
        {bannerUrl ? (
          <div className="h-32 sm:h-40 overflow-hidden">
            <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className={cn('h-32 sm:h-40 bg-gradient-to-br', bannerGradient)} />
        )}

        {/* Banner controls overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-foreground/0 group-hover/banner:bg-foreground/30 transition-colors">
          <div className="flex gap-2 opacity-70 group-hover/banner:opacity-100 transition-opacity">
            <div className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer shadow-md transition-colors">
              <svg className="w-4 h-4 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="pointer-events-none">{t('userprofile.changeBanner')}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onBannerChange(f);
                  e.target.value = '';
                }}
              />
            </div>
            {bannerUrl && (
              <Button size="sm" variant="danger" onClick={onBannerRemove} className="shadow-md">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 pb-5 sm:px-6 sm:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-14">
          {/* Avatar — click opens gallery or upload */}
          <div
            className="shrink-0 self-center sm:self-auto cursor-pointer"
            onClick={onAvatarClick}
          >
            <div className={cn(
              'relative w-32 h-32 rounded-full overflow-hidden border-2 transition-colors border-transparent hover:border-primary/50 group/avatar',
            )}>
              <EntityAvatar name={displayName || initials} image={avatarUrl} resource={profile?.avatar} className="h-full w-full border-0 text-2xl" />
              {/* Upload overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onAvatarUpload(f); e.target.value = ''; }}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 text-center sm:text-left pb-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                {displayName || t('userprofile.profileSettings')}
              </h1>
              <Badge variant={status.variant as 'success' | 'warning' | 'error' | 'default'}>
                {t(status.labelKey)}
              </Badge>
            </div>
            {profile?.bio && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{profile.bio}</p>
            )}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              {profile?.location && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  {profile.location}
                </span>
              )}
              {profile?.language && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                  {profile.language}
                </span>
              )}
              {profile?.created_at && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {t('userprofile.memberSince')} {new Date(profile.created_at as string).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

// ─── Main Page ───────────────────────────────────────────────────
export const ProfileSettingsPage = observer(() => {
  const profileStore = useUserprofile();
  const avatarStore = useUserprofileAvatar();
  const bannerStore = useUserprofileBanner();
  const settingStore = useUserprofileSetting();
  const { t } = useI18n();
  const auth = useAuth();

  const profile = profileStore.currentItem as Userprofile | null;
  const profileId = profile?.entity?.id ?? '';

  // Load profile on mount
  useEffect(() => {
    if (auth.activeProfileId) {
      profileStore.GetUserprofile(auth.activeProfileId);
    } else if (auth.userId) {
      profileStore.GetUserprofileDefault(auth.userId);
    }
  }, [auth.activeProfileId, auth.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Form state ──────────────────────────────
  const [form, setForm] = useState({
    firstName: '', lastName: '', middleName: '', displayName: '',
    bio: '', location: '', gender: '', dateOfBirth: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  // ─── Banner state ─────────────────────────────
  const [bannerGradient, setBannerGradient] = useState<string>(bannerGradients[0].value);
  const [banners, setBanners] = useState<Banner[]>([]);

  // ─── Avatar state ────────────────────────────
  const [avatars, setAvatars] = useState<Avatar[]>([]);

  // ─── Feedback state ──────────────────────────
  const [success, setSuccess] = useState('');
  const [error, setError] = useState<unknown>(null);

  // ─── Your profiles (multi-profile switching / activation) ──────
  const [myProfiles, setMyProfiles] = useState<Userprofile[]>([]);
  const [defaultProfileId, setDefaultProfileId] = useState<string | null>(null);
  const [profileActiveMap, setProfileActiveMap] = useState<Record<string, boolean>>({});
  const [profilesBusyId, setProfilesBusyId] = useState<string | null>(null);
  const [profilesError, setProfilesError] = useState<unknown>(null);

  const loadMyProfiles = useCallback(async () => {
    if (!auth.userId) return;
    setProfilesError(null);
    try {
      const listResp = await ListUserprofilesForService(auth.userId, { pagination: { limit: 50, cursor: '', direction: '' } } as never);
      const profiles = listResp.userprofiles?.profiles ?? [];
      setMyProfiles(profiles);

      const defaultIdResp = await profileStore.GetUserprofileDefaultID(auth.userId);
      setDefaultProfileId(defaultIdResp.profile_id || null);

      // Refresh each profile's activation status from the server rather than
      // trusting a possibly-stale local flag.
      const entries = await Promise.all(
        profiles.map(async (p) => {
          const id = p.entity?.id;
          if (!id) return null;
          try {
            const resp = await GetUserprofileActive(id);
            return [id, resp.userprofile?.status !== 'PROFILE_STATUS_INACTIVE'] as const;
          } catch {
            return [id, true] as const;
          }
        }),
      );
      setProfileActiveMap(Object.fromEntries(entries.filter(Boolean) as [string, boolean][]));
    } catch (err) {
      setProfilesError(err);
    }
  }, [auth.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMyProfiles();
  }, [loadMyProfiles]);

  const handleSetDefault = async (targetProfileId: string) => {
    if (!auth.userId) return;
    setProfilesBusyId(targetProfileId);
    try {
      await profileStore.SetUserprofileDefault(auth.userId, targetProfileId, {
        user: { id: auth.userId } as never,
        profile: { id: targetProfileId } as never,
      });
      setDefaultProfileId(targetProfileId);
    } catch (err) {
      setProfilesError(err);
    } finally {
      setProfilesBusyId(null);
    }
  };

  const handleActivateProfile = async (targetProfileId: string) => {
    setProfilesBusyId(targetProfileId);
    try {
      await profileStore.ActivateUserprofile(targetProfileId, { profile: { id: targetProfileId } as never });
      const resp = await GetUserprofileActive(targetProfileId);
      setProfileActiveMap((prev) => ({ ...prev, [targetProfileId]: resp.userprofile?.status !== 'PROFILE_STATUS_INACTIVE' }));
    } catch (err) {
      setProfilesError(err);
    } finally {
      setProfilesBusyId(null);
    }
  };

  // ─── Load profile data ───────────────────────
  useEffect(() => {
    if (profile && !loaded) {
      setForm({
        firstName: profile.first_name ?? '',
        lastName: profile.last_name ?? '',
        middleName: profile.middle_name ?? '',
        displayName: profile.display_name ?? '',
        bio: profile.bio ?? '',
        location: profile.location ?? '',
        gender: profile.gender ?? '',
        dateOfBirth: profile.date_of_birth ? String(profile.date_of_birth).slice(0, 10) : '',
      });
      setLoaded(true);
    }
  }, [profile, loaded]);

  // ─── Load settings + avatars ──────────────────
  useEffect(() => {
    if (!profileId) return;

    // Reset stores to avoid showing stale data from another profile
    avatarStore.reset();
    bannerStore.reset();
    setAvatars([]);
    setBanners([]);
    setLoaded(false);

    settingStore.GetUISettings(profileId)
      .then((resp: { settings?: Record<string, string> }) => {
        if (resp.settings?.banner_gradient) {
          setBannerGradient(resp.settings.banner_gradient);
        }
      })
      .catch(() => {});

    loadAvatars();
    loadBanners();
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvatars = useCallback(async () => {
    if (!profileId) return;
    try {
      await avatarStore.ListAvatars(profileId, { pagination: { limit: 50, cursor: '', direction: '' } } as never);
      setAvatars([...avatarStore.items] as Avatar[]);
    } catch { /* handled by store */ }
  }, [profileId, avatarStore]);

  const loadBanners = useCallback(async () => {
    if (!profileId) return;
    try {
      await bannerStore.ListBanners(profileId, { pagination: { limit: 50, cursor: '', direction: '' } } as never);
      setBanners([...bannerStore.items] as Banner[]);
    } catch { /* handled by store */ }
  }, [profileId, bannerStore]);

  // ─── Handlers ────────────────────────────────
  const showSuccess = (key: string) => {
    setSuccess(key);
    setError(null);
  };
  const showError = (err: unknown) => {
    setError(err);
    setSuccess('');
  };

  const handleFormChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSaveProfile = async () => {
    if (!profileId) return;
    const validation = validateProfile({ ...form, language: '', timezone: '' });
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      await profileStore.UpdateUserprofile(profileId, {
        userprofile: {
          ...(profile ?? {}),
          first_name: form.firstName, last_name: form.lastName,
          middle_name: form.middleName, display_name: form.displayName,
          bio: form.bio, location: form.location,
          gender: form.gender, date_of_birth: form.dateOfBirth ? `${form.dateOfBirth}T00:00:00Z` : undefined,
        } as never,
      });
      showSuccess('userprofile.saveSuccess');
    } catch (err) { showError(err); }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!profileId) return;
    const validation = validateAvatar(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    try {
      const avatar = await avatarStore.CreateAvatar(file);
      if (avatar?.entity?.id) {
        await avatarStore.ActivateAvatar(profileId, avatar.entity.id, { profile: {} as never, avatar: {} as never });
      }
      await loadAvatars();
      showSuccess('userprofile.saveSuccess');
    } catch (err) { showError(err); }
  };

  const handleBannerUpload = async (file: File) => {
    if (!profileId) return;
    const validation = validateBanner(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    try {
      const banner = await bannerStore.CreateBanner(file);
      if (banner?.entity?.id) {
        await bannerStore.ActivateBanner(profileId, banner.entity.id, { profile: {} as never, banner: {} as never });
      }
      await loadBanners();
      showSuccess('userprofile.bannerSaved');
    } catch (err) { showError(err); }
  };

  const handleBannerRemove = async () => {
    if (!profileId) return;
    const activeBanner = banners.find((b) => b.is_active);
    if (!activeBanner?.entity?.id) return;
    try {
      await bannerStore.DeleteBanner(profileId, activeBanner.entity.id);
      await loadBanners();
      showSuccess('userprofile.bannerRemoved');
    } catch (err) { showError(err); }
  };

  const handleActivateAvatar = async (avatarId: string) => {
    try {
      await avatarStore.ActivateAvatar(profileId, avatarId, { profile: {} as never, avatar: {} as never });
      await loadAvatars();
    } catch (err) { showError(err); }
  };

  const handleDeleteAvatar = async (avatarId: string) => {
    try {
      await avatarStore.DeleteAvatar(profileId, avatarId);
      setAvatars((prev) => prev.filter((a) => a.entity?.id !== avatarId));
    } catch (err) { showError(err); }
  };

  // Viewer state for avatar gallery (VK-style)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const activeAvatar = avatars.find((a) => a.is_active) ?? avatars[0];
  const activeAvatarUrl = activeAvatar?.url || activeAvatar?.thumbnail_url;
  const activeBanner = banners.find((b) => b.is_active) ?? banners[0];
  const activeBannerUrl = activeBanner?.url;

  if (profileStore.isLoadingItem || !profile) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Feedback */}
      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}
      {success && <Alert variant="success" dismissible onDismiss={() => setSuccess('')}>{t(success)}</Alert>}

      {/* Profile header */}
      <ProfileHeader
        profile={profile}
        avatarUrl={activeAvatarUrl}
        bannerUrl={activeBannerUrl}
        bannerGradient={bannerGradient}
        onAvatarClick={() => avatars.length > 0 ? setViewerIndex(0) : undefined}
        onAvatarUpload={handleAvatarUpload}
        onBannerChange={handleBannerUpload}
        onBannerRemove={handleBannerRemove}
        t={t}
      />

      {/* Your profiles — switch, set default, activate (multi-profile accounts) */}
      {myProfiles.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.yourProfilesTitle')}</CardTitle>
            <CardDescription>{t('userprofile.yourProfilesDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {!!profilesError && <ErrorAlert error={profilesError} dismissible onDismiss={() => setProfilesError(null)} />}
            {myProfiles.map((p) => {
              const id = p.entity?.id ?? '';
              const isDefault = id === defaultProfileId;
              const isActive = profileActiveMap[id] ?? true;
              return (
                <div key={id} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {p.display_name || `${p.first_name} ${p.last_name}`.trim() || id.slice(0, 8)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {isDefault && <Badge variant="secondary" size="sm">{t('userprofile.defaultProfile')}</Badge>}
                      {!isActive && <Badge variant="warning" size="sm">{t('userprofile.inactiveProfile')}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isDefault && (
                      <Button variant="ghost" size="sm" onClick={() => handleSetDefault(id)} isLoading={profilesBusyId === id}>
                        {t('userprofile.setAsDefault')}
                      </Button>
                    )}
                    {!isActive && (
                      <Button variant="secondary" size="sm" onClick={() => handleActivateProfile(id)} isLoading={profilesBusyId === id}>
                        {t('userprofile.activateProfile')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Personal Info */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.nameSection')}</CardTitle>
            <CardDescription>{t('userprofile.nameSectionDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label={t('userprofile.firstName')} value={form.firstName} onChange={handleFormChange('firstName')} error={errors.firstName} required />
              <Input label={t('userprofile.lastName')} value={form.lastName} onChange={handleFormChange('lastName')} error={errors.lastName} required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label={t('userprofile.middleName')} value={form.middleName} onChange={handleFormChange('middleName')} />
              <Input label={t('userprofile.displayName')} value={form.displayName} onChange={handleFormChange('displayName')} error={errors.displayName} placeholder={t('userprofile.displayNamePlaceholder')} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('userprofile.aboutSection')}</CardTitle>
            <CardDescription>{t('userprofile.aboutSectionDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              label={t('userprofile.bio')}
              value={form.bio}
              onChange={handleFormChange('bio')}
              error={errors.bio}
              helperText={`${form.bio.length}/256`}
              rows={4}
              placeholder={t('userprofile.bioPlaceholder')}
            />
            <Input label={t('userprofile.location')} value={form.location} onChange={handleFormChange('location')} placeholder={t('userprofile.locationPlaceholder')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('userprofile.gender')}</label>
                <Select value={form.gender} onChange={handleFormChange('gender')}>
                  {genderOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                  ))}
                </Select>
              </div>
              <Input label={t('userprofile.dateOfBirth')} type="date" value={form.dateOfBirth} onChange={handleFormChange('dateOfBirth')} />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-2 border-t border-border pt-4">
            <Button onClick={handleSaveProfile} isLoading={profileStore.isSaving}>{t('common.saveChanges')}</Button>
          </CardFooter>
        </Card>
      </div>

      {/* Avatar Gallery */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">{t('userprofile.avatarGallery')}</CardTitle>
            <CardDescription>{t('userprofile.avatarGalleryDesc')}</CardDescription>
          </div>
          <div className="relative">
            <Button size="sm" variant="secondary">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('userprofile.uploadAvatar')}
            </Button>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarUpload(f); e.target.value = ''; }}
            />
          </div>
        </CardHeader>
        <CardContent>
          {avatars.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">{t('userprofile.noAvatars')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {avatars.map((avatar, idx) => {
                const imgUrl = avatar.url || avatar.thumbnail_url;
                return (
                  <div key={avatar.entity?.id} className="relative group">
                    <div
                      className={cn(
                        'w-full aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer',
                        avatar.is_active
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-transparent hover:border-primary/50',
                      )}
                      onClick={() => setViewerIndex(idx)}
                    >
                      <img src={imgUrl} alt={avatar.original_filename} className="w-full h-full object-cover" />
                    </div>

                    {/* Active badge */}
                    {avatar.is_active && (
                      <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-primary rounded-full border-2 border-card">
                        <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* Filename below */}
                    <p className="mt-1 text-[11px] text-muted-foreground truncate text-center" title={avatar.original_filename}>
                      {avatar.original_filename || avatar.entity?.id?.slice(0, 8)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* VK-style Avatar Viewer */}
      {viewerIndex !== null && avatars.length > 0 && (
        <AvatarViewer
          avatars={avatars}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onActivate={handleActivateAvatar}
          onDelete={handleDeleteAvatar}
          isOwner
          ownerName={profile?.display_name || `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim()}
          t={t}
        />
      )}
    </div>
  );
});

export default ProfileSettingsPage;
